import { useMemo, useState } from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Stack, Tab, Tabs, Typography } from '@mui/material';

import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import LoadingPanel from 'src/pages/admin/components/LoadingPanel';
import {
  listClosures,
  listLanePauses,
  listFreezes,
  listHistory,
  listQuarantinedTransfers,
  listHeldOrders,
  listWaitingForSigners,
  listWorkOrders,
} from 'src/pages/admin/services/vaultOperationsService';
import type { PermissionData } from 'src/pages/admin/entities/permission.entity';
import { hasPermission } from 'src/pages/admin/utils/has-permission';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionGuard } from 'src/routes/components/permission-guard';

import { ALL_VAULTS_VALUE } from '../vaults/constants';
import { managedVaults } from '../vaults/helpers';
import { useVaults } from '../vaults/useVaults';
import { VaultSwitcher } from '../vaults/VaultSwitcher';
import { ClosuresTab } from './ClosuresTab';
import {
  HISTORY_PAGE_SIZE,
  OPERATIONS_NS as NS,
  OPERATIONS_PERMISSION_RESOURCE as RESOURCE,
  OPERATIONS_TABS,
  OUTCOME_BUTTON_SX,
  SIGNERS_PERMISSION_RESOURCE,
  TAB_PERMISSION_RESOURCE,
  type OperationsTab,
} from './constants';
import { FrozenTab } from './FrozenTab';
import { HeldTab } from './HeldTab';
import { HistoryTab, type HistoryFilter } from './HistoryTab';
import { LanePauseBanner } from './LanePauseBanner';
import { checkedTime } from './parts';
import { PayoutsTab } from './PayoutsTab';
import { SignersTab } from './SignersTab';
import { TransfersTab } from './TransfersTab';
import { useQueue, type Queue } from './useQueue';

const isTab = (value: string | null): value is OperationsTab =>
  OPERATIONS_TABS.some((tab) => tab === value);

/** A tab's body: its error with a retry, the loading panel on the first read, else the tab. */
const TabBody = ({ queue, children }: { queue: Queue<unknown>; children: React.ReactNode }) => {
  const translate = useTranslate();
  return (
    <>
      {queue.error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" sx={OUTCOME_BUTTON_SX} onClick={queue.refetch}>
              {translate(`${NS}.retry`)}
            </Button>
          }
        >
          {queue.error}
        </Alert>
      )}
      <LoadingPanel isLoading={queue.loading && queue.data === null && !queue.error}>
        {!queue.error && children}
      </LoadingPanel>
    </>
  );
};

/**
 * XBIT-4870: the Operations console. One page, a tab per queue with counts (ops-console-ux rule 6); each item says what happened,
 * who is affected, what to do and by when, and every action states what money moves before it is sent. Every list is read
 * by the BFF of XBIT-4869 and re-read every 30 s; an action re-reads all of them.
 */
const OperationsPage = () => {
  const translate = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  // Each tab is shown, and its list read, only to a role holding the tab's own permission (the queues: vault:operations; Waiting
  // for signers: vault:approvals, the former Approvals page). The page opens for either; the BFF still checks every call.
  const { data: permissionData } = usePermissions<PermissionData>();
  const allowed = (value: OperationsTab) =>
    hasPermission(permissionData?.permissions ?? [], TAB_PERMISSION_RESOURCE(value), 'read');
  const canSeeQueues = allowed('frozen');
  const canSeeSigners = allowed('signers');
  const visibleTabs = OPERATIONS_TABS.filter(allowed);
  const [chosenTab, setChosenTab] = useState<OperationsTab>(
    isTab(requested) ? requested : OPERATIONS_TABS[0]
  );
  // a tab asked for in the address that this role may not see: say so instead of showing another one silently
  const refusedTab = isTab(requested) && !visibleTabs.includes(requested) ? requested : null;
  // the address carries the tab, so a refresh or a pasted link opens the same one (the old /vault/approvals bookmark lands on signers)
  const setTab = (next: OperationsTab) => {
    setChosenTab(next);
    setSearchParams({ tab: next }, { replace: true });
  };
  // no visible tab (neither permission): the guard below shows the 403, this only keeps the hooks above it from reading `undefined`
  const tab: OperationsTab = visibleTabs.includes(chosenTab)
    ? chosenTab
    : (visibleTabs[0] ?? OPERATIONS_TABS[0]);
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const { vaults, loading: vaultsLoading, loadError: vaultsError } = useVaults(canSeeQueues);
  const choices = useMemo(() => managedVaults(vaults), [vaults]);
  const names = useMemo(() => new Map(vaults.map((v) => [v.vaultId, v.name])), [vaults]);
  const vaultName = (vaultId: string | null | undefined) =>
    vaultId ? (names.get(vaultId) ?? vaultId) : '—';
  // "All vaults" is itself an explicit choice; a per-vault action always takes the vault of its own item.
  const [vaultChoice, setVaultChoice] = useState<string>(ALL_VAULTS_VALUE);
  const vaultFilter = vaultChoice === ALL_VAULTS_VALUE ? '' : vaultChoice;
  const [history, setHistory] = useState<HistoryFilter>({ queue: '', status: '', offset: 0 });

  const freezes = useQueue(() => listFreezes(), [], canSeeQueues);
  const payouts = useQueue(() => listWorkOrders(), [], canSeeQueues);
  const closures = useQueue(() => listClosures(), [], canSeeQueues);
  const signers = useQueue(() => listWaitingForSigners(), [], canSeeSigners);
  // same route and so the same permission as the signers tab
  const held = useQueue(() => listHeldOrders(), [], canSeeSigners);
  const lanePauses = useQueue(() => listLanePauses(), [], canSeeSigners);
  const transfers = useQueue(
    () => listQuarantinedTransfers(vaultFilter || undefined),
    [vaultFilter],
    canSeeQueues
  );
  const historyQueue = useQueue(
    () =>
      listHistory({
        queue: history.queue || undefined,
        status: history.status || undefined,
        vaultId: vaultFilter || undefined,
        offset: history.offset,
        limit: HISTORY_PAGE_SIZE,
      }),
    [history.queue, history.status, history.offset, vaultFilter],
    canSeeQueues
  );

  const queues: Record<OperationsTab, Queue<{ total: number; checkedAt: string }>> = {
    frozen: freezes,
    held,
    payouts,
    closures,
    transfers,
    signers,
    history: historyQueue,
  };
  const refreshAll = () =>
    Promise.all([...Object.values(queues), lanePauses].map((queue) => queue.refetch())).then(
      () => undefined
    );
  const count = (key: OperationsTab) => queues[key].data?.total;
  const checkedAt = queues[tab].data?.checkedAt;
  const showsVaultFilter = tab === 'transfers' || tab === 'history';

  return (
    <PermissionGuard
      anyOf={[
        { resource: RESOURCE, action: 'read' },
        { resource: SIGNERS_PERMISSION_RESOURCE, action: 'read' },
      ]}
      fallback={<ErrorPage403 />}
    >
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate(`${NS}.title`)}
          links={[
            { name: translate('resource.vault'), href: '#' },
            { name: translate(`${NS}.title`) },
          ]}
          action={
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              {checkedAt && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {translate(`${NS}.lastChecked`, { time: checkedTime(checkedAt) })}
                </Typography>
              )}
              <Button
                sx={OUTCOME_BUTTON_SX}
                variant="outlined"
                startIcon={<Iconify icon="solar:refresh-bold" />}
                onClick={refreshAll}
              >
                {translate(`${NS}.refresh`)}
              </Button>
            </Stack>
          }
        />

        <LanePauseBanner queue={lanePauses} onSettled={refreshAll} />

        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mt: 3 }}
        >
          {visibleTabs.map((value) => {
            const n = count(value);
            return (
              <Tab
                key={value}
                value={value}
                label={`${translate(`${NS}.tabs.${value}`)}${n === undefined ? '' : ` (${n})`}`}
              />
            );
          })}
        </Tabs>

        <Stack spacing={2} sx={{ mt: 3 }}>
          {refusedTab && !noticeDismissed && (
            <Alert
              severity="info"
              onClose={() => setNoticeDismissed(true)}
              data-testid="tab-refused"
            >
              {translate(`${NS}.notices.tabRefused`, {
                tab: translate(`${NS}.tabs.${refusedTab}`),
              })}
            </Alert>
          )}
          {vaultsError && <Alert severity="error">{vaultsError}</Alert>}
          {showsVaultFilter && (
            <VaultSwitcher
              vaults={choices}
              value={vaultChoice}
              allowAll
              onChange={(next) => {
                setVaultChoice(next);
                setHistory((cur) => ({ ...cur, offset: 0 }));
              }}
              disabled={vaultsLoading}
            />
          )}
          {tab === 'frozen' && (
            <TabBody queue={freezes}>
              {freezes.data && <FrozenTab queue={freezes} vaultName={vaultName} />}
            </TabBody>
          )}
          {tab === 'held' && (
            <TabBody queue={held}>
              {held.data && (
                <HeldTab
                  queue={held}
                  vaultName={vaultName}
                  topUpWaiting={signers.data?.items.some((row) => row.lane === 'COLD_TO_HOT')}
                />
              )}
            </TabBody>
          )}
          {tab === 'payouts' && (
            <TabBody queue={payouts}>
              {payouts.data && (
                <PayoutsTab queue={payouts} vaultName={vaultName} onSettled={refreshAll} />
              )}
            </TabBody>
          )}
          {tab === 'closures' && (
            <TabBody queue={closures}>
              {closures.data && (
                <ClosuresTab queue={closures} vaultName={vaultName} onSettled={refreshAll} />
              )}
            </TabBody>
          )}
          {tab === 'transfers' && (
            <TabBody queue={transfers}>
              {transfers.data && (
                <TransfersTab queue={transfers} vaultName={vaultName} onSettled={refreshAll} />
              )}
            </TabBody>
          )}
          {tab === 'signers' && (
            <TabBody queue={signers}>
              {signers.data && <SignersTab queue={signers} vaultName={vaultName} />}
            </TabBody>
          )}
          {tab === 'history' && (
            <TabBody queue={historyQueue}>
              {historyQueue.data && (
                <HistoryTab
                  queue={historyQueue}
                  filter={history}
                  onFilter={setHistory}
                  vaultName={vaultName}
                />
              )}
            </TabBody>
          )}
        </Stack>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default OperationsPage;
